import { max } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";

import { InsightsAsset } from "graphql-types/graphql";
import { useAssetSalesPriceQuery } from "legacyNext/hooks";
import { useAssetStore } from "legacyNext/store/asset-page-store";

import { S } from "./economy.styles";
import { FinancingGraph } from "./FinancingGraph";
import { GreenFinancingHero } from "./GreenFinancingHero";
import { GreenLoanCalculator } from "./GreenLoanCalculator";
import { KPITable } from "./KPITable/KPITable";
import { LoanInvestmentSettings } from "./types";

export const Economy = () => {
    const { asset } = useOutletContext<{ asset: InsightsAsset }>();

    const { proposals } = useAssetStore();

    const { data: assetSalesResponse, loading: assetSalesLoading } =
        useAssetSalesPriceQuery(asset.externalId);

    const [loanInvestmentSettings, setLoanInvestmentSettings] =
        useState<LoanInvestmentSettings>(() => {
            const initialState = {
                propertyPrice: 0,
                valueImpact: 20,
                improvementLoan: 0,
                establishmentCosts: 35000,
                equity: 0,
                annualInterestRate: 3.25,
                interestOnlyPeriod: 0,
                loanTerm: 30,
                salesDate: null,
                currency: null,
            };

            if (!assetSalesResponse) return initialState;

            const { salesPrice, salesDate, currency } = assetSalesResponse;
            const propertyPrice = Number(salesPrice);
            const equity = Math.round(propertyPrice * 0.6);

            return {
                ...initialState,
                propertyPrice,
                equity,
                salesDate: salesDate ?? null,
                currency,
            };
        });

    useEffect(() => {
        if (!assetSalesResponse || assetSalesLoading) return;

        const { salesPrice, salesDate, currency } = assetSalesResponse;

        const propertyPrice = Number(salesPrice);
        const equity = Math.round(propertyPrice * 0.6);

        setLoanInvestmentSettings((prevState) => ({
            ...prevState,
            propertyPrice,
            equity,
            salesDate: salesDate ?? null,
            currency,
        }));
    }, [assetSalesResponse, assetSalesLoading]);

    useEffect(() => {
        if (!proposals.length) return;

        const enabledProposals = proposals.filter(
            (proposal) => proposal.enabled && proposal.startYear
        );

        const totalInvestment = enabledProposals.reduce(
            (sum, proposal) => sum + (Number(proposal.investment) ?? 0),
            0
        );

        const newValues = {
            ...loanInvestmentSettings,
            improvementLoan: totalInvestment,
        };
        setLoanInvestmentSettings(newValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proposals]);

    const impactYear = useMemo(() => {
        const startYears = proposals
            .filter((proposal) => proposal.enabled && proposal.startYear)
            .map((proposal) => proposal.startYear);

        return max(startYears) ?? null;
    }, [proposals]);

    return (
        <S.Economy>
            <FinancingGraph
                impactYear={impactYear}
                loanInvestmentSettings={loanInvestmentSettings}
                loading={assetSalesLoading}
            />
            <GreenLoanCalculator
                loanInvestmentSettings={loanInvestmentSettings}
                setLoanInvestmentSettings={setLoanInvestmentSettings}
            />
            <GreenFinancingHero />
            <KPITable assetId={asset.id} area={asset.area ?? null} />
        </S.Economy>
    );
};
